import { buildTaggingResultRowId, type TaggingResultRow } from "./constants";
import {
  searchAutoTagResults,
  type TaggingResultDisplayConcept,
  type TaggingResultsSearchFilter,
} from "../../services/taggingResultsService";
import { downloadStyledExcel } from "../../utils/downloadStyledExcel";

export type { TaggingResultDisplayConcept };

export type TaggingResultFilter = Readonly<{
  objectType: string;
  objectSubType: string;
  conceptCode: string;
  conceptValue: string;
  objectId?: string;
}>;

export type TaggingResultPageQuery = TaggingResultFilter &
  Readonly<{
    pageIndex: number;
    pageSize: number;
  }>;

export type TaggingResultPage = Readonly<{
  rows: TaggingResultRow[];
  displayConcepts: TaggingResultDisplayConcept[];
  total: number;
}>;

export function canFetchTaggingResultsList(
  objectType: string,
  objectSubType: string,
  objectSubTypeCount: number,
): boolean {
  if (!objectType.trim()) return false;
  if (objectSubTypeCount > 0 && !objectSubType.trim()) return false;
  return true;
}

export function optionLabel(
  options: ReadonlyArray<{ code: string; name: string }>,
  code: string,
): string {
  return options.find((option) => option.code === code)?.name?.trim() || "";
}

function normalizeFilterValue(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Client-side filter helper for tests and CSV fixtures.
 */
export function filterTaggingResults(
  rows: TaggingResultRow[],
  filter: TaggingResultFilter,
): TaggingResultRow[] {
  const objectType = normalizeFilterValue(filter.objectType);
  const objectSubType = normalizeFilterValue(filter.objectSubType);
  const conceptCode = normalizeFilterValue(filter.conceptCode);

  return rows.filter((row) => {
    if (objectType && normalizeFilterValue(row.objectType) !== objectType) {
      return false;
    }
    if (
      objectSubType &&
      normalizeFilterValue(row.objectSubType) !== objectSubType
    ) {
      return false;
    }
    if (
      conceptCode &&
      !row.conceptCodes.some(
        (code) => normalizeFilterValue(code) === conceptCode,
      )
    ) {
      return false;
    }
    return true;
  });
}

function toSearchFilter(query: TaggingResultPageQuery): TaggingResultsSearchFilter {
  return {
    objectType: query.objectType,
    objectSubType: query.objectSubType,
    conceptCode: query.conceptCode,
    conceptValue: query.conceptValue,
    objectId: query.objectId,
    pageIndex: query.pageIndex,
    pageSize: query.pageSize,
  };
}

export async function fetchTaggingResultPage(
  query: TaggingResultPageQuery,
): Promise<TaggingResultPage> {
  const page = await searchAutoTagResults(toSearchFilter(query));
  return {
    rows: page.items.map((item) => ({
      id: buildTaggingResultRowId(item.id, item.version),
      objectId: item.id,
      objectType: item.objectType,
      objectTypeName: item.objectTypeName,
      objectSubType: item.objectSubType,
      objectSubTypeName: item.objectSubTypeName,
      conceptCodes: item.conceptCodes,
      title: item.title,
      version: item.version,
      tagsByConcept: item.tagsByConcept ?? {},
      link: item.link ?? "",
    })),
    displayConcepts: page.displayConcepts ?? [],
    total: page.total,
  };
}

function escapeCsvCell(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export type TaggingResultExportLabels = Readonly<{
  objectTypeLabel?: string;
  objectSubTypeLabel?: string;
}>;

function conceptCellValue(
  row: TaggingResultRow,
  conceptName: string,
): string {
  return row.tagsByConcept[conceptName]?.trim() || "-";
}

/** Prefer filter labels (same as the table); fall back to row API names. */
export function exportScopeLabel(
  label: string | undefined,
  fallback: string,
): string {
  if (label !== undefined) {
    return label.trim() || "-";
  }
  return fallback.trim() || "-";
}

function exportRowValues(
  row: TaggingResultRow,
  displayConcepts: readonly TaggingResultDisplayConcept[],
  labels: TaggingResultExportLabels,
): string[] {
  return [
    exportScopeLabel(labels.objectTypeLabel, row.objectTypeName),
    exportScopeLabel(labels.objectSubTypeLabel, row.objectSubTypeName),
    row.objectId,
    row.title,
    row.version,
    ...displayConcepts.map((concept) => conceptCellValue(row, concept.name)),
  ];
}

export function taggingResultsToCsv(
  rows: TaggingResultRow[],
  displayConcepts: readonly TaggingResultDisplayConcept[] = [],
  labels: TaggingResultExportLabels = {},
): string {
  const header = [
    "Object Type",
    "Object Sub-Type",
    "Object ID",
    "Title",
    "Version",
    ...displayConcepts.map((concept) => concept.displayName),
  ];
  const lines = rows.map((row) =>
    exportRowValues(row, displayConcepts, labels).map(escapeCsvCell).join(","),
  );
  return [header.join(","), ...lines].join("\n");
}

export function downloadTaggingResultsExcel(
  rows: TaggingResultRow[],
  displayConcepts: readonly TaggingResultDisplayConcept[] = [],
  labels: TaggingResultExportLabels = {},
): Promise<void> {
  return downloadStyledExcel({
    entityName: "Classification Result",
    sheetName: "Classification Result",
    columns: [
      { header: "Object Type", width: 16 },
      { header: "Object Sub-Type", width: 18 },
      { header: "Object ID", width: 16 },
      { header: "Title", width: 40 },
      { header: "Version", width: 12 },
      ...displayConcepts.map((concept) => ({
        header: concept.displayName,
        width: 24,
      })),
    ],
    rows: rows.map((row) => exportRowValues(row, displayConcepts, labels)),
  });
}
